/**
 * FBA Inbound v2024 API Service
 * All API calls routed through backend for security
 */

const API_URL = import.meta.env.VITE_API_URL || '';

// Generic fetch wrapper with error handling
const apiFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('API Error Response:', {
      url,
      status: response.status,
      errorData
    });
    throw new Error(errorData.message || errorData.error || JSON.stringify(errorData) || `API Error: ${response.status}`);
  }

  return response.json();
};

// Warehouse address IDs
export const WAREHOUSE_ADDRESSES = {
  CA: 11,           // Canada warehouse
  US: 63577,        // US warehouse
  ABDUL_ANUM: 63818 // Abdul/Anum Basit special
};

// Transport modes
export const TRANSPORT_MODES = {
  PARTNERED_SPD: 'PARTNERED_GROUND_SMALL_PARCEL',
  NON_PARTNERED_SPD: 'NON_PARTNERED_SMALL_PARCEL',
  NON_PARTNERED_LTL: 'NON_PARTNERED_LTL'
};

// ============================================
// Address Assignment (Step 1)
// ============================================

export const setShipFromAddress = async (outboundShipmentId, shipFromAddressId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/set-ship-from`, {
    method: 'POST',
    body: JSON.stringify({
      outbound_shipment_id: outboundShipmentId,
      ship_from_address_id: shipFromAddressId
    })
  });
};

// ============================================
// Plan Management (Step 2)
// ============================================

export const createInboundPlan = async (outboundShipmentId, channelId, items) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans`, {
    method: 'POST',
    body: JSON.stringify({
      outbound_shipment_id: outboundShipmentId,
      channel_id: channelId,
      using_2d_barcodes: false,
      items: items.map(item => ({
        msku: item.msku,
        quantity: item.quantity,
        expiration: item.expiration || null,
        label_owner: item.label_owner || 'SELLER',
        prep_owner: item.prep_owner || 'SELLER'
      }))
    })
  });
};

export const getPlan = async (planId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}`);
};

export const getPlansForOutbound = async (outboundShipmentId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/by-outbound/${outboundShipmentId}`);
};

export const getActivePlans = async (page = 1, perPage = 20) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/active?page=${page}&per_page=${perPage}`);
};

export const deletePlan = async (planId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/cancel`, {
    method: 'POST'
  });
};

export const batchCancelPlans = async (planIds) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/batch-cancel`, {
    method: 'POST',
    body: JSON.stringify({ planIds })
  });
};

// Trigger packing options generation
export const triggerPackingOptions = async (planId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/packing-options`);
};

// ============================================
// Packing & Box Content (Step 3)
// ============================================

export const createBoxGroup = async (planId, boxGroupData) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/box-groups`, {
    method: 'POST',
    body: JSON.stringify(boxGroupData)
  });
};

export const deleteBoxGroup = async (planId, boxGroupId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/box-groups/${boxGroupId}`, {
    method: 'DELETE'
  });
};

export const addItemsToBoxGroup = async (planId, boxGroupId, items) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/box-groups/${boxGroupId}/items`, {
    method: 'POST',
    body: JSON.stringify({ items })
  });
};

export const setBoxQuantity = async (planId, boxGroupId, quantity) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/box-groups/${boxGroupId}/set-box-quantity`, {
    method: 'POST',
    body: JSON.stringify({ quantity })
  });
};

export const setPackingInformation = async (planId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/set-packing`, {
    method: 'POST',
    body: JSON.stringify({ plan_id: planId })
  });
};

export const addCartonRequestToSubmission = async (planId, packingGroupIds) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/add-carton-request`, {
    method: 'POST',
    body: JSON.stringify({ packing_group_ids: packingGroupIds })
  });
};

export const confirmPackingOption = async (planId, packingOptionId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/confirm-packing`, {
    method: 'POST',
    body: JSON.stringify({ plan_id: planId, packing_option_id: packingOptionId })
  });
};

export const markPackingOptionAsConfirmed = async (planId, packingOptionId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/mark-packing-confirmed`, {
    method: 'POST',
    body: JSON.stringify({ plan_id: planId, packing_option_id: packingOptionId })
  });
};

// ============================================
// Operations Status
// ============================================

export const checkOperationStatus = async (operationId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/operations/${operationId}/update`);
};

export const queryOperations = async (operationIds = [], page = 1, perPage = 100) => {
  let query = '';
  if (operationIds.length > 0) {
    query = operationIds.map(id => `id:"${id}"`).join(' OR ');
  }
  return apiFetch(`${API_URL}/api/fba-inbound/operations?page=${page}&per_page=${perPage}&q=${encodeURIComponent(query)}`);
};

export const getAllOperations = async (page = 1, perPage = 50) => {
  return apiFetch(`${API_URL}/api/fba-inbound/operations?page=${page}&per_page=${perPage}`);
};

export const hasOngoingOperation = async (operationIds) => {
  if (!operationIds || operationIds.length === 0) return false;
  try {
    const result = await queryOperations(operationIds);
    const operations = result.data || [];
    return operations.some(op => op.status === 'IN_PROGRESS');
  } catch (err) {
    console.error('Error checking ongoing operations:', err);
    return false;
  }
};

export const waitForOperations = async (operationIds, maxWaitMs = 120000) => {
  const startTime = Date.now();
  while (Date.now() - startTime < maxWaitMs) {
    const hasOngoing = await hasOngoingOperation(operationIds);
    if (!hasOngoing) {
      return true;
    }
    await new Promise(resolve => setTimeout(resolve, 2000));
  }
  return false;
};

// Existing operation status endpoint (from server.js:9509)
export const getOperationStatus = async (operationId) => {
  return apiFetch(`${API_URL}/api/operation_status/${operationId}`);
};

// ============================================
// Placement Options (Step 4)
// ============================================

export const triggerPlacementOptions = async (planId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/placement-options`);
};

export const getShipmentDetails = async (planId, placementOptionId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/shipment-details?placement_option_id=${placementOptionId}`);
};

export const listShipmentBoxes = async (planId, placementOptionId, shipmentId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/shipment-boxes?placement_option_id=${placementOptionId}&shipment_id=${shipmentId}`);
};

export const generatePlacementOptions = async (planId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/generate-placement`, {
    method: 'POST',
    body: JSON.stringify({ plan_id: planId })
  });
};

export const selectPlacementOption = async (planId, placementOptionId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/select-placement`, {
    method: 'POST',
    body: JSON.stringify({ plan_id: planId, placement_option_id: placementOptionId })
  });
};

export const markPlacementOptionAsConfirmed = async (planId, placementOptionId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/mark-placement-confirmed`, {
    method: 'POST',
    body: JSON.stringify({ plan_id: planId, placement_option_id: placementOptionId })
  });
};

// ============================================
// Transportation Options
// ============================================

export const generateTransportationOptions = async (planId, placementOptionId, shipmentsWithDates) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/generate-transportation`, {
    method: 'POST',
    body: JSON.stringify({
      plan_id: planId,
      placement_option_id: placementOptionId,
      shipments_dates: shipmentsWithDates
    })
  });
};

export const fetchTransportationOptions = async (planId, placementOptionId, shipmentId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/transportation-options?placement_option_id=${placementOptionId}&shipment_id=${shipmentId}`);
};

export const setBoxesIds = async (planId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/set-boxes-ids`);
};

export const confirmTransportationOption = async (planId, placementOptionId, shipmentTransportationIds) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/confirm-transportation`, {
    method: 'POST',
    body: JSON.stringify({
      plan_id: planId,
      placement_option_id: placementOptionId,
      shipment_transportation_ids: shipmentTransportationIds
    })
  });
};

export const markTransportationOptionAsConfirmed = async (planId, shipmentTransportationIds) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/mark-transportation-confirmed`, {
    method: 'POST',
    body: JSON.stringify({
      plan_id: planId,
      shipment_transportation_ids: shipmentTransportationIds
    })
  });
};

// ============================================
// Transport Mode (Step 5)
// ============================================

export const setTransportModePreference = async (planId, placementOptionId, shipmentId, transportMode) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/transport-mode`, {
    method: 'POST',
    body: JSON.stringify({
      plan_id: planId,
      placement_option_id: placementOptionId,
      shipment_id: shipmentId,
      transport_mode_preference: transportMode
    })
  });
};

export const confirmPlacementOption = async (planId, placementOptionId, shipmentId = null) => {
  const body = {
    plan_id: planId,
    placement_option_id: placementOptionId
  };
  if (shipmentId) {
    body.shipment_id = shipmentId;
  }
  console.log('confirmPlacementOption request:', body);
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/confirm-placement`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

// ============================================
// Tracking Details (Step 6)
// ============================================

export const updateTrackingSPD = async (planId, trackingDetails) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/tracking-spd`, {
    method: 'POST',
    body: JSON.stringify(trackingDetails)
  });
};

export const updateTrackingLTL = async (planId, trackingDetails) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/tracking-ltl`, {
    method: 'POST',
    body: JSON.stringify(trackingDetails)
  });
};

// ============================================
// Labels (Step 7)
// ============================================

export const requestBoxLabels = async (planId, shipmentId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/plans/${planId}/request-labels`, {
    method: 'POST',
    body: JSON.stringify({ shipment_id: shipmentId })
  });
};

export const getLabels = async (planId, shipmentId = null) => {
  let url = `${API_URL}/api/fba-inbound/plans/${planId}/labels`;
  if (shipmentId) {
    url += `?shipment_id=${shipmentId}`;
  }
  return apiFetch(url);
};

// ============================================
// Outbound Shipment Data
// ============================================

export const getOutboundShipment = async (outboundId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/outbound/${outboundId}`);
};

export const getOutboundItems = async (outboundId) => {
  return apiFetch(`${API_URL}/api/fba-inbound/outbound/${outboundId}/items`);
};

export const searchOutbounds = async (searchTerm = '') => {
  return apiFetch(`${API_URL}/api/get_outbounds?search=${encodeURIComponent(searchTerm)}`);
};

// ============================================
// Helper functions
// ============================================

export const getClients = async () => {
  return apiFetch(`${API_URL}/api/get_clients`);
};

export default {
  // Address
  setShipFromAddress,
  WAREHOUSE_ADDRESSES,

  // Plan
  createInboundPlan,
  getPlan,
  getPlansForOutbound,
  getActivePlans,
  deletePlan,
  batchCancelPlans,
  triggerPackingOptions,

  // Packing
  createBoxGroup,
  deleteBoxGroup,
  addItemsToBoxGroup,
  setBoxQuantity,
  setPackingInformation,
  addCartonRequestToSubmission,
  confirmPackingOption,
  markPackingOptionAsConfirmed,

  // Operations
  checkOperationStatus,
  queryOperations,
  getAllOperations,
  hasOngoingOperation,
  waitForOperations,
  getOperationStatus,

  // Placement
  triggerPlacementOptions,
  getShipmentDetails,
  listShipmentBoxes,
  generatePlacementOptions,
  selectPlacementOption,
  markPlacementOptionAsConfirmed,

  // Transportation
  generateTransportationOptions,
  fetchTransportationOptions,
  setBoxesIds,
  confirmTransportationOption,
  markTransportationOptionAsConfirmed,

  // Transport
  TRANSPORT_MODES,
  setTransportModePreference,
  confirmPlacementOption,

  // Tracking
  updateTrackingSPD,
  updateTrackingLTL,

  // Labels
  requestBoxLabels,
  getLabels,

  // Outbound
  getOutboundShipment,
  getOutboundItems,
  searchOutbounds,

  // Helpers
  getClients
};
